import { Modal, ModalDialog, ModalOverflow, Typography, Stack, Box, Button, Card, CardContent, Chip, CircularProgress, Alert, ModalClose, FormControl, FormLabel, Input } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useContext } from "react";
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import PhoneIcon from '@mui/icons-material/Phone';
import { LayoutContext } from "../context";
import { APIGetTariffPlans, APICreateTariffPayment, APICheckPaymentStatus } from "../api";



const TariffModal = ({ onPaymentSuccess }) => {
  const [step, setStep] = useState('selection');
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneRequired, setPhoneRequired] = useState(false);

  const { token, currentUser, tariffModalOpen, setTariffModalOpen } = useContext(LayoutContext);

  useEffect(() => {
    if (tariffModalOpen) {
      loadTariffs();
      resetModal();
      // Проверяем, есть ли у пользователя телефон
      checkPhoneRequirement();
    }
  }, [tariffModalOpen, currentUser]);

  const checkPhoneRequirement = () => {
    // Если у пользователя нет телефона, показываем поле для ввода
    if (!currentUser?.phone) {
      setPhoneRequired(true);
    } else {
      setPhoneRequired(false);
      setPhone(currentUser.phone);
    }
  };

  const resetModal = () => {
    setStep('selection');
    setSelectedTariff(null);
    setSelectedDuration(null);
    setError('');
    setPhone('');
  };

  const loadTariffs = async () => {
    try {
      setLoading(true);
      const response = await APIGetTariffPlans(token);
      if (response.success) {
        setTariffs(response.tariffs);
      }
    } catch (error) {
      setError('Ошибка загрузки тарифов');
    } finally {
      setLoading(false);
    }
  };

  const handleTariffSelect = (tariff, duration) => {
    setSelectedTariff(tariff);
    setSelectedDuration(duration);
  };

  const validatePhone = (phoneNumber) => {
    // Простая валидация российского номера телефона
    const phoneRegex = /^(\+7|7|8)?[\s\-]?\(?[489][0-9]{2}\)?[\s\-]?[0-9]{3}[\s\-]?[0-9]{2}[\s\-]?[0-9]{2}$/;
    return phoneRegex.test(phoneNumber.replace(/\s/g, ''));
  };

  const handlePayment = async () => {
    if (!selectedTariff || !selectedDuration) return;

    // Проверяем телефон, если он требуется
    if (phoneRequired && !phone) {
      setError('Пожалуйста, укажите номер телефона');
      return;
    }

    if (phoneRequired && !validatePhone(phone)) {
      setError('Пожалуйста, укажите корректный номер телефона');
      return;
    }

    try {
      setProcessingPayment(true);
      setError('');
      
      const paymentData = {
        tariff_plan: selectedTariff.id,
        duration_days: parseInt(selectedDuration)
      };

      // Добавляем телефон в данные платежа, если он указан
      if (phone) {
        paymentData.phone = phone;
      }
      
      const response = await APICreateTariffPayment(token, paymentData);
      
      if (response.success) {
        // Открываем страницу оплаты в новой вкладке
        window.open(response.payment_url, '_blank');
        
        // Переходим к шагу ожидания оплаты
        setStep('payment');
        
        // Начинаем проверку статуса платежа
        checkPaymentStatus(response.payment.uuid_token);
      } else {
        setError(response.error || 'Ошибка создания платежа');
      }
    } catch (error) {
      setError('Ошибка создания платежа');
    } finally {
      setProcessingPayment(false);
    }
  };

  const checkPaymentStatus = async (paymentUuid) => {
    try {
      const response = await APICheckPaymentStatus(token, paymentUuid);
      
      if (response.success) {
        if (response.payment.status === 'paid') {
          setStep('success');
          if (onPaymentSuccess) {
            onPaymentSuccess();
          }
        } else if (response.payment.status === 'pending') {
          // Продолжаем проверку
          setTimeout(() => checkPaymentStatus(paymentUuid), 3000);
        }
      }
    } catch (error) {
      console.error('Ошибка проверки платежа:', error);
    }
  };

  const formatPhone = (value) => {
    // Форматирование номера телефона по мере ввода
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.startsWith('7') || numbers.startsWith('8')) {
      // Российский формат: +7 (XXX) XXX-XX-XX
      if (numbers.length <= 1) return '+7';
      if (numbers.length <= 4) return `+7 (${numbers.slice(1, 4)}`;
      if (numbers.length <= 7) return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}`;
      if (numbers.length <= 9) return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}-${numbers.slice(7, 9)}`;
      return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}-${numbers.slice(7, 9)}-${numbers.slice(9, 11)}`;
    } else {
      // Международный формат
      if (numbers.length <= 3) return `+${numbers}`;
      if (numbers.length <= 5) return `+${numbers.slice(0, 3)} ${numbers.slice(3)}`;
      if (numbers.length <= 8) return `+${numbers.slice(0, 3)} ${numbers.slice(3, 5)} ${numbers.slice(5)}`;
      return `+${numbers.slice(0, 3)} ${numbers.slice(3, 5)} ${numbers.slice(5, 8)} ${numbers.slice(8)}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhone(e.target.value);
    setPhone(formattedPhone);
  };

  const getTariffIcon = (tariffId) => {
    switch (tariffId) {
      case 'standard':
        return <StarRoundedIcon />;
      case 'premium':
        return <WorkspacePremiumRoundedIcon />;
      case 'vip':
        return <CheckCircleRoundedIcon />;
      default:
        return <StarRoundedIcon />;
    }
  };

  const getTariffColor = (tariffId) => {
    switch (tariffId) {
      case 'standard':
        return 'primary';
      case 'premium':
        return 'success';
      case 'vip':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <AnimatePresence>
      {tariffModalOpen && (
        <Modal open={tariffModalOpen} onClose={() => setTariffModalOpen(false)} sx={{ zIndex: 10001 }}>
          <ModalOverflow sx={{ height: "auto" }}>
            <ModalDialog
              component={motion.div}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              sx={{
                maxWidth: 800,
                width: '95%',
                maxHeight: '90vh',
                p: 3,
                borderRadius: 'xl'
              }}
            >
              <ModalClose onClick={() => setTariffModalOpen(false)} />
              
              {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {step === 'selection' && (
                <TariffSelectionStep
                  tariffs={tariffs}
                  selectedTariff={selectedTariff}
                  selectedDuration={selectedDuration}
                  onTariffSelect={handleTariffSelect}
                  onPayment={handlePayment}
                  loading={loading}
                  processingPayment={processingPayment}
                  getTariffIcon={getTariffIcon}
                  getTariffColor={getTariffColor}
                  currentUser={currentUser}
                  phoneRequired={phoneRequired}
                  phone={phone}
                  onPhoneChange={handlePhoneChange}
                />
              )}
              
              {step === 'payment' && (
                <PaymentProcessingStep
                  selectedTariff={selectedTariff}
                  selectedDuration={selectedDuration}
                />
              )}
              
              {step === 'success' && (
                <PaymentSuccessStep
                  selectedTariff={selectedTariff}
                  selectedDuration={selectedDuration}
                  onClose={onClose}
                />
              )}
            </ModalDialog>
          </ModalOverflow>
        </Modal>
      )}
    </AnimatePresence>
  );
};

// Обновленный компонент выбора тарифа с полем телефона
const TariffSelectionStep = ({ tariffs, selectedTariff, selectedDuration, onTariffSelect, onPayment, loading, processingPayment, getTariffIcon, getTariffColor, currentUser, phoneRequired, phone, onPhoneChange }) => {
  return (
    <>
      <Typography level="h3" sx={{ textAlign: 'center', mb: 0, mt: 2 }}>
        Выберите тариф
      </Typography>
      <Typography level="body-sm" color="neutral" sx={{ textAlign: 'center', mb: 3 }}>
        Расширьте возможности для знакомств
      </Typography>

      {/* Поле для телефона, если требуется */}
      {phoneRequired && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <Card variant="soft" sx={{ mb: 3, backgroundColor: 'warning.100' }}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PhoneIcon color="warning" />
                  <Typography level="title-sm" color="warning">
                    Требуется номер телефона
                  </Typography>
                </Stack>
                <Typography level="body-sm">
                  Для оформления платежа через ЮKassa необходим ваш номер телефона
                </Typography>
                <FormControl required>
                  <FormLabel>Номер телефона</FormLabel>
                  <Input
                    type="tel"
                    placeholder="+7 (999) 123-45-67"
                    value={phone}
                    onChange={onPhoneChange}
                    color="warning"
                    startDecorator={<PhoneIcon />}
                    sx={{ maxWidth: 300 }}
                  />
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <Typography level="body-md" sx={{ fontWeight: 600, opacity: 0.5, lineHeight: 1.2, mb: 2 }}>
        Выберите тариф и нажмите на "Стоимость"
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          {tariffs.map((tariff, index) => (
            <motion.div
              key={tariff.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                variant={selectedTariff?.id === tariff.id ? "outlined" : "soft"}
                sx={{
                  borderColor: selectedTariff?.id === tariff.id ? `${getTariffColor(tariff.id)}.500` : 'transparent',
                  borderWidth: 2,
                  borderRadius: 'lg',
                }}
              >
                <CardContent>
                  <Stack spacing={2}>
                    {/* Заголовок тарифа */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        sx={{
                          color: `${getTariffColor(tariff.id)}.500`,
                          fontSize: '2rem'
                        }}
                      >
                        {getTariffIcon(tariff.id)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography level="title-lg">
                          {tariff.name}
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                          {tariff.description}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Особенности тарифа */}
                    <Stack spacing={1}>
                      <Typography level="body-sm" fontWeight="bold">
                        Включено:
                      </Typography>
                      <Stack spacing={0.5}>
                        <FeatureItem text="5 лайков/день" included={true} />
                        <FeatureItem text="5 супер лайков/день" included={true} />
                        <FeatureItem 
                          text="Создание групп" 
                          included={tariff.features.can_create_groups} 
                        />
                        <FeatureItem 
                          text="Свидание за наш счет (90 дней)" 
                          included={tariff.features.free_dating_event} 
                        />
                        <FeatureItem 
                          text="Поднятие анкеты 1 раз/3 дня" 
                          included={tariff.features.profile_boost} 
                        />
                      </Stack>
                    </Stack>

                    {/* Варианты продолжительности */}
                    <Stack spacing={1}>
                      <Typography level="body-sm" fontWeight="bold">
                        Стоимость:
                      </Typography>
                      <Stack direction="row" sx={{ gap: .5 }} flexWrap="wrap">
                        {Object.entries(tariff.prices).map(([days, price]) => (
                          <Chip
                            key={days}
                            variant={selectedTariff?.id === tariff.id && selectedDuration === days ? "solid" : "outlined"}
                            color={getTariffColor(tariff.id)}
                            onClick={() => onTariffSelect(tariff, days)}
                            sx={{ cursor: 'pointer', mb: 0.2 }}
                          >
                            {days} дней - {formatPrice(price)}
                          </Chip>
                        ))}
                      </Stack>
                    </Stack>

                    {/* Кнопка выбора */}
                    {selectedTariff?.id === tariff.id && selectedDuration && (
                      <Button
                        variant="solid"
                        color={getTariffColor(tariff.id)}
                        onClick={onPayment}
                        loading={processingPayment}
                        disabled={phoneRequired && !phone}
                        sx={{ mt: 1 }}
                      >
                        {phoneRequired && !phone ? 'Укажите телефон' : 'Выбрать тариф'}
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Stack>
      )}
    </>
  );
};

// Компонент отображения особенности
const FeatureItem = ({ text, included }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <CheckCircleRoundedIcon
      sx={{
        fontSize: '1rem',
        color: included ? 'success.500' : 'neutral.300'
      }}
    />
    <Typography
      level="body-sm"
      sx={{
        color: included ? 'text.primary' : 'neutral.400',
        textDecoration: included ? 'none' : 'line-through'
      }}
    >
      {text}
    </Typography>
  </Stack>
);

// Компонент обработки платежа
const PaymentProcessingStep = ({ selectedTariff, selectedDuration }) => (
  <Stack spacing={3} alignItems="center" textAlign="center">
    <CircularProgress size="lg" />
    <Typography level="h4">Ожидание оплаты</Typography>
    <Typography level="body-md" color="neutral">
      Ожидаем подтверждения оплаты тарифа {selectedTariff?.name} на {selectedDuration} дней
    </Typography>
    <Typography level="body-sm" color="neutral">
      Если страница оплаты не открылась, проверьте блокировку всплывающих окон
    </Typography>
  </Stack>
);

// Компонент успешной оплаты
const PaymentSuccessStep = ({ selectedTariff, selectedDuration, onClose }) => (
  <Stack spacing={3} alignItems="center" textAlign="center">
    <CheckCircleRoundedIcon 
      sx={{ fontSize: 64, color: 'success.500' }} 
    />
    <Typography level="h3" color="success">
      Тариф активирован!
    </Typography>
    <Typography level="body-lg">
      Теперь у вас есть доступ ко всем функциям тарифа {selectedTariff?.name}
    </Typography>
    <Button size="lg" onClick={onClose}>
      Понятно
    </Button>
  </Stack>
);

// Вспомогательная функция форматирования цены
const formatPrice = (price) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0
  }).format(price);
};

export default TariffModal;